import {
  getFormattedListTimeZones,
  getTimeZoneFromFormattedList,
} from 'app/ProviderSearch/utils'
import Select from 'react-select'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { useContext, useMemo, useState } from 'react'
import { minimalTimezoneSet } from 'constants/common'
import type { Placement } from 'common/CustomTooltip'
import classNames from 'classnames'
import styles from './index.module.scss'
import { customStyles } from './styles'
import {
  BLACK_PEARL,
  LIGHT_GREY,
  WHITE,
  NAVY,
  DIM_GREY,
  BRIGHT_BLUE,
} from 'constants/colors'

type TimeZonePickerProps = {
  className?: string
  containerClass?: string
  timezoneInfoClass?: string
  tooltipDirection?: Placement
  showClientMsgHint?: boolean
  showToolTip?: boolean
  /** Boxed trigger look: 10px corners, #666 border, navy chevron, #000 value. Options unchanged. */
  isBoxed?: boolean
}

const TimeZonePicker: React.FC<TimeZonePickerProps> = ({
  className = '',
  containerClass,
  timezoneInfoClass = 'justify-content-end',
  showClientMsgHint = true,
  isBoxed = false,
}) => {
  const { timeZoneObject, updateTimezone } = useContext(TimeZoneContext)

  const [menuPlacement, setMenuPlacement] = useState('bottom')

  const timeZoneSelectItem = useMemo(() => {
    if (timeZoneObject) {
      return getTimeZoneFromFormattedList(
        timeZoneObject.tzName,
        timeZoneObject.tzCode
      )
    }

    return null
  }, [timeZoneObject])

  const onChangeTimeZone = ({ value, tzCode }) => {
    const timeZoneObject = minimalTimezoneSet.find(
      (timeZoneObject) =>
        timeZoneObject.tzName === value && timeZoneObject.tzCode === tzCode
    )
    updateTimezone(timeZoneObject)
  }

  const isOptionSelected = (
    option: TimezoneSelectItem,
    selectValue: TimezoneSelectItem[]
  ): boolean => {
    const selectedValue = selectValue[0]
    const doesCodeMatch = option.tzCode === selectedValue?.tzCode
    const doesNameMatch = option.value === selectedValue?.value
    return doesCodeMatch && doesNameMatch
  }

  const timeZoneOptions = useMemo(() => {
    return getFormattedListTimeZones()
  }, [])

  //TODO: Restructure component
  return (
    <div
      data-testid="timezone-selector"
      className={classNames(
        containerClass,
        styles.TimeZonePicker,
        // Profile mobile: drop the legacy 24px wrapper margin — the modal owns spacing.
        isBoxed && '!~m-0'
      )}
    >
      {showClientMsgHint && (
        <div className={`${styles.ClientMessage} ${timezoneInfoClass}`}>
          <div className={styles.ClientMessage_text}>
            Clients will be booked in this timezone
          </div>
        </div>
      )}
      <Select
        aria-label="timezone selector"
        isOptionSelected={isOptionSelected}
        cacheOptions
        inputId="timezone-picker"
        placeholder="Timezone"
        className={classNames(`${styles.TimeZonePicker_input}  ${className}`)}
        value={timeZoneSelectItem}
        onChange={onChangeTimeZone}
        options={timeZoneOptions}
        menuPlacement="auto"
        theme={(theme) => ({
          ...theme,
        })}
        styles={{
          ...customStyles,
          control: (baseStyles, state) => {
            const { menuIsOpen } = state

            if (isBoxed) {
              // Profile design: 10px corners + charcoal (#666) stroke, no open-state morph.
              return {
                ...baseStyles,
                border: `1px solid ${DIM_GREY}`,
                boxShadow: 'none',
                borderRadius: '10px',
                backgroundColor: WHITE,
                height: '50px',
                '&:hover': {
                  border: `1px solid ${DIM_GREY}`,
                },
              }
            }

            return {
              ...baseStyles,
              border: `1px solid ${LIGHT_GREY}`,
              boxShadow: 'none',
              borderRadius: '100px',
              backgroundColor: WHITE,
              height: '50px',
              '&:hover': {
                border: `1px solid ${LIGHT_GREY}`,
              },
              ...(menuIsOpen && {
                borderRadius:
                  menuPlacement === 'top'
                    ? '0px 0px 25px 25px'
                    : '25px 25px 0px 0px',
                transition: 'border-radius 0.1s ease-out',
              }),
            }
          },
          singleValue: (baseStyles) => {
            const styled = customStyles.singleValue(baseStyles)

            if (isBoxed) {
              // Profile design: #000, weight 400, 0.08px tracking (Gibson/16px from container).
              return {
                ...styled,
                color: '#000',
                fontWeight: '400',
                letterSpacing: '0.08px',
              }
            }

            return styled
          },
          dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            color: isBoxed ? NAVY : BRIGHT_BLUE,
          }),
          menu: (baseStyles, state) => {
            // since state.placement value is not avaliable for "control" element, had to create seperate state
            if (state.placement) {
              setMenuPlacement(state.placement)
            }

            return {
              ...baseStyles,
              color: BLACK_PEARL,
              margin: '0px',
              border: `1px solid ${LIGHT_GREY}`,
              borderTop: 'none',
              fontWeight: '400',
              boxShadow:
                menuPlacement === 'top'
                  ? '0px -3px 6px 0px rgba(33, 37, 41, 0.12)'
                  : '0px 3px 6px 0px rgba(33, 37, 41, 0.12)',
              borderRadius:
                menuPlacement === 'top'
                  ? '25px 25px 0px 0px'
                  : '0px 0px 25px 25px',
              overflow: 'hidden',
            }
          },
          menuList: (baseStyles) => {
            return {
              ...baseStyles,
              borderRadius:
                menuPlacement === 'top'
                  ? '25px 25px 0px 0px'
                  : '0px 0px 25px 25px',
              paddingBottom: '0px',
              paddingTop: '0px',
            }
          },
        }}
        components={{
          IndicatorSeparator: () => null,
        }}
      />
    </div>
  )
}

export default TimeZonePicker
